import React from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ImageBackground,
} from "react-native";
import { ArrowRight, X, Pencil } from "lucide-react-native";
import {
  useNavigation,
  useRoute,
  type NavigationProp,
  type ParamListBase,
  type RouteProp,
} from "@react-navigation/native";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useTranslations } from "@src/packages/i18n";
import { useLocale } from "@src/packages/i18n/useLocale";
import { SECTION_REVIEW_CONFIGS } from "./reviewRegistry";
import type { ReviewItem } from "./reviewRegistry";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  EMBER,
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const velvetCopper =
  require("@louise/moon-ui/assets/images/velvet-copper-btn.jpg") as number;

type ReviewParamList = {
  SectionReview: { sectionKey: string };
};

function ReviewRow({
  item,
  isLast,
  onPress,
  emptyLabel,
}: {
  item: ReviewItem;
  isLast: boolean;
  onPress: () => void;
  emptyLabel: string;
}) {
  const capturePress = usePressCapture("SectionReviewRow");
  return (
    <Pressable
      onPress={() => {
        capturePress(pressLabel(item.label), "review_item_row");
        onPress();
      }}
      style={
        isLast
          ? undefined
          : {
              borderBottomWidth: 1,
              borderBottomColor: "rgba(255,255,255,0.08)",
            }
      }
    >
      <View className="flex-row items-center gap-3 px-4 py-3.5">
        <View className="flex-1">
          <Text
            className="font-dm-sans mb-0.5 text-[13px] font-medium"
            style={{ color: EMBER.colors.textLight }}
            numberOfLines={1}
          >
            {item.label}
          </Text>
          <Text
            className="font-dm-sans text-[15px] font-semibold"
            style={{
              color: item.value ? EMBER.colors.text : EMBER.colors.textFaint,
            }}
            numberOfLines={2}
          >
            {item.value || emptyLabel}
          </Text>
        </View>

        <View className="h-7 w-7 items-center justify-center rounded-full bg-white/15">
          <Pencil size={12} color={EMBER.colors.textLight} strokeWidth={2} />
        </View>
      </View>
    </Pressable>
  );
}

export function SectionReviewScreen() {
  const navigation = useNavigation<NavigationProp<ParamListBase>>();
  const route = useRoute<RouteProp<ReviewParamList, "SectionReview">>();
  const t = useTranslations();
  const capturePress = usePressCapture("SectionReviewScreen");
  const { locale } = useLocale();
  const sectionKey: string = route.params?.sectionKey;
  const { data: patient } = usePatient();

  const config = SECTION_REVIEW_CONFIGS[sectionKey];

  if (!config || !patient) {
    return null;
  }

  const items = config.getItems(patient, t, locale);

  const handleEditItem = (item: ReviewItem) => {
    // Navigate to the section flow, optionally targeting a specific screen
    if (item.screenName) {
      navigation.navigate(item.flowRoute, { screen: item.screenName });
    } else {
      navigation.navigate(item.flowRoute);
    }
  };

  const handleEditAll = () => {
    navigation.navigate(config.flowRoute);
  };

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Header */}
        <View
          style={{
            paddingTop: SCREEN_PADDING_TOP,
            paddingHorizontal: 16,
            paddingBottom: 8,
          }}
        >
          {/* Title row with close button */}
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 4,
            }}
          >
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 24,
                fontWeight: "700",
                color: EMBER.colors.text,
                letterSpacing: -0.5,
                flex: 1,
              }}
            >
              {t(config.titleKey)}
            </Text>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t("med_history.overview.back")}
              onPress={() => {
                // Chevron-only control; its accessibility label is the copy.
                capturePress(
                  pressLabel(t("med_history.overview.back")),
                  "med_history.overview.back",
                );
                navigation.goBack();
              }}
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                backgroundColor: "rgba(255,255,255,0.15)",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <X size={18} color={EMBER.colors.text} strokeWidth={2} />
            </Pressable>
          </View>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              color: EMBER.colors.textLight,
              marginBottom: 20,
            }}
          >
            {t("med_history.shared.tap_to_update")}
          </Text>
        </View>

        {/* Review items */}
        <View style={{ paddingHorizontal: 16 }}>
          <View style={{ ...GLASS_CARD_STYLE, padding: 0, overflow: "hidden" }}>
            {items.map((item, i) => (
              <ReviewRow
                key={`${item.label}-${i}`}
                item={item}
                isLast={i === items.length - 1}
                onPress={() => handleEditItem(item)}
                emptyLabel={t("med_history.shared.not_provided")}
              />
            ))}
          </View>

          {/* Edit all button */}
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("med_history.shared.edit_all")),
                "med_history.shared.edit_all",
              );
              handleEditAll();
            }}
            style={{
              marginTop: 16,
              borderRadius: 14,
              overflow: "hidden",
            }}
          >
            <ImageBackground
              source={velvetCopper}
              resizeMode="cover"
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                paddingVertical: 16,
                paddingHorizontal: 24,
                gap: 8,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 16,
                  fontWeight: "500",
                  color: "#FFFFFF",
                }}
              >
                {t("med_history.shared.edit_all")}
              </Text>
              <ArrowRight size={18} color="#FFFFFF" strokeWidth={2} />
            </ImageBackground>
          </Pressable>
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}
